import PropTypes from 'prop-types'
import { Button, message, Switch, Table } from 'antd'
import {
  CheckCircleOutlined,
  FileExcelOutlined,
  FileSearchOutlined,
} from '@ant-design/icons'
import { useCallback, useEffect, useMemo } from 'react'
import queryString from 'query-string'
import get from 'lodash/get'
import { useDispatch } from 'react-redux'
import { createUseStyles, useTheme } from 'react-jss'
import { Link } from 'react-router-dom'
import { useIsAdmin } from '../../redux/slices/session'
import { useAsync } from '../../hooks'
import {
  updatePreAuthorization,
  useFetchPreAuthorizations,
  usePreAuthorizations,
  usePreAuthorizationsPagination,
} from '../../redux/slices/pre-authorizations'
import { ListError } from '../Common'
import { toPath } from '../../routes'
import { formatDOB } from '../../lib/datetime'
import useExportExcel from '../../hooks/useExportExcel'
import DownloadLink from '../Common/DownloadLink'
import filenameFromBlobPath from '../../lib/active-storage'
import CircleCheckIcon from '../Common/CircleCheckIcon'

const useStyles = createUseStyles({
  rejected: {
    textAlign: 'center',
    color: 'red',
  },
})

const BASE_COLUMNS = [
  {
    title: 'Patient',
    dataIndex: ['patient', 'lastName'],
    render(text, preAuthorization) {
      return (
        <Link to={toPath.patients.patient({ id: preAuthorization.patient.id })}>
          {preAuthorization.patient.lastName}
          {', '}
          {preAuthorization.patient.firstName}
        </Link>
      )
    },
  },
  {
    title: 'DOB',
    key: 'dob',
    dataIndex: ['patient', 'birthDate'],
    render: formatDOB,
  },
  {
    title: 'Device Type',
    dataIndex: ['device', 'deviceModel', 'deviceType', 'name'],
    key: 'deviceType',
  },
  {
    title: 'CPT Code',
    dataIndex: ['cptCode'],
    key: 'cptCode',
  },
  {
    title: 'ICD-10 Code',
    dataIndex: ['icd10Code'],
    key: 'icd10Code',
  },
  {
    title: 'Tech DOS',
    key: 'billingDate',
    render(text, preAuthorization) {
      if (!preAuthorization.billingDate) return ''
      return formatDOB(preAuthorization.billingDate)
    },
  },
  {
    title: 'Auth #',
    key: 'auth',
    render(_text, preAuthorization) {
      const { insuranceAuthorizationFilePath, insuranceAuthorizationNumber } =
        preAuthorization
      if (!insuranceAuthorizationFilePath) return insuranceAuthorizationNumber

      return (
        <DownloadLink path={insuranceAuthorizationFilePath}>
          <span>
            {insuranceAuthorizationNumber ||
              filenameFromBlobPath(insuranceAuthorizationFilePath)}
          </span>
        </DownloadLink>
      )
    },
  },
  {
    title: 'Notes',
    dataIndex: 'notes',
    key: 'notes',
    render(notes, preAuthorization) {
      const { notesFilePath } = preAuthorization
      if (!notesFilePath) return notes

      return (
        <DownloadLink path={notesFilePath}>
          <span>{notes || filenameFromBlobPath(notesFilePath)}</span>
        </DownloadLink>
      )
    },
  },
]

export default function PreAuthorizations({
  cycleId,
  onPageChange,
  page = 1,
  filters = {},
  rowSelection = null
}) {
  const classes = useStyles()
  const isAdmin = useIsAdmin()
  const { brandWarning } = useTheme()
  const preAuthorizations = usePreAuthorizations(cycleId)
  const { pageSize, total } = usePreAuthorizationsPagination(cycleId)
  const { pending, error, clearError, execute } = useAsync(
    useFetchPreAuthorizations(cycleId, page, filters),
    false,
  )
  const dispatch = useDispatch()

  useEffect(() => {
    execute(cycleId, page, filters)
  }, [execute, cycleId, page, filters])

  const handleAuthorizedChange = useCallback(
    async (id, authorized) => {
      try {
        await dispatch(updatePreAuthorization(id, { authorized }))
        message.success('Remote authorization successfully updated')
      } catch (e) {
        message.error(
          get(
            e,
            'response.errors.authorized',
            'Oops, we had a problem. Please try submitting again',
          ),
        )
      }
    },
    [dispatch],
  )

  const columns = useMemo(() => {
    const filesColumns = [
      {
        title: 'Report',
        dataIndex: ['hasReportFile'],
        key: 'hasReportFile',
        align: 'center',
        render(text, { hasReportFile }) {
          return hasReportFile === null ? (
            <span>-</span>
          ) : (
            <CircleCheckIcon
              checked={hasReportFile}
              falseStyle={{ color: brandWarning }}
            />
          )
        },
      },
      {
        title: 'Transmission',
        dataIndex: ['hasReportRawFile'],
        key: 'hasReportRawFile',
        align: 'center',
        render(text, { hasReportRawFile }) {
          if (hasReportRawFile === null) {
            return <span>-</span>
          }
          return (
            <CircleCheckIcon
              checked={hasReportRawFile}
              falseStyle={{ color: brandWarning }}
            />
          )
        },
      },
    ]

    return [
      {
        title: 'MRN',
        dataIndex: ['patient', 'practiceMrn'],
        key: 'patientMrn',
      },
      ...BASE_COLUMNS,
      {
        title: 'ICM Only',
        key: 'rejected',
        className: classes.rejected,
        render(_text, preAuthorization) {
          const { rejectedAt } = preAuthorization

          return rejectedAt ? <CheckCircleOutlined /> : null
        },
      },
      {
        title: 'Authorized',
        key: 'authorized',
        render: (text, record) => (
          <Switch
            checked={record.authorized}
            onChange={authorized =>
              handleAuthorizedChange(record.id, authorized)
            }
          />
        ),
      },
      ...(isAdmin ? filesColumns : []),
      {
        title: 'Actions',
        key: 'actions',
        render(text, preAuthorization) {
          return (
            <Link
              to={toPath.preAuthorizations.preAuthorization({
                id: preAuthorization.id,
              })}
            >
              <Button type="link" icon={<FileSearchOutlined />} />
            </Link>
          )
        },
      },
    ]
  }, [brandWarning, classes.rejected, handleAuthorizedChange, isAdmin])

  const exportUrl = useMemo(
    () =>
      queryString.stringifyUrl(
        {
          url: `/cycles/${cycleId}/pre-authorizations.xlsx`,
          query: {
            q: filters?.q,
            authorized: filters?.authorized,
            office_location_id: filters?.officeLocationId,
            no_full_reports: filters?.noFullReports,
          },
        },
        { skipNull: true, skipEmptyString: true },
      ),
    [cycleId, filters],
  )

  const [exportingExcel, exportExcel] = useExportExcel(exportUrl)

  return (
    <>
      {error && <ListError description={error.message} onClose={clearError} />}
      <Table
        size="middle"
        columns={columns}
        dataSource={preAuthorizations}
        rowKey="id"
        loading={pending}
        rowSelection={rowSelection}
        pagination={{
          current: page,
          pageSize,
          onChange: onPageChange,
          total,
        }}
      />
      {Boolean(preAuthorizations.length) && (
        <div>
          <Button
            loading={exportingExcel}
            onClick={exportExcel}
            icon={<FileExcelOutlined />}
          >
            Export this list to Excel
          </Button>
        </div>
      )}
    </>
  )
}

PreAuthorizations.propTypes = {
  cycleId: PropTypes.string.isRequired,
  onPageChange: PropTypes.func.isRequired,
  rowSelection: PropTypes.shape({}),
  page: PropTypes.number,
  filters: PropTypes.shape({
    q: PropTypes.string,
    authorized: PropTypes.bool,
    officeLocationId: PropTypes.string,
    noFullReports: PropTypes.bool,
  }),
}

